import { useState } from 'react';
import { FaWhatsapp,  FaArrowRight, FaArrowLeft, FaPaperPlane, FaPaperclip } from 'react-icons/fa';
import { FiMail, FiPhone, FiMessageCircle } from 'react-icons/fi';
import apiClient from '../api/apiClient';
import { postContactMessage } from '../api/publicApi';

/* Allowed MIME types per API spec */
const ALLOWED_MIMETYPES = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'application/pdf', 'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const CONTACTS = [
  {
    icon: <FaWhatsapp size={18} color='#25D366' />,
    label: 'Temoignages',
    value: '+225 07 47 90 94 98',
    href: 'https://wa.me/2250747909498',
  },
  {
    icon: <FiMail size={18} color='#8b1512'/>,
    label: 'Support par e-mail',
    href: 'mailto:contact@ministerecatholiquemisericorde.com',
  },
  {
    icon: <FaWhatsapp size={18} color='#25D366'/>,
    label: 'Autres sujets - Secretariat officiel',
    value: '+225 07 49 15 54 15',
    href: 'https://wa.me/2250749155415',
  },
];

const SUBJECTS = [
  // { value: 'testimony', label: 'Témoignage' },
  { value: 'prayer_request', label: 'Demande de prière' },
  { value: 'information', label: 'Information' },
  { value: 'other', label: 'Autre' }
];

/* ── Shared message form ── */
function MessageForm({ onBack, mobile }) {
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [telephone, setTelephone] = useState('');
  const [subject, setSubject] = useState('');
  const [msg, setMsg] = useState('');
  const [file, setFile] = useState(null);

  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // null | 'uploading' | 'done' | 'error'
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);

  /* ── Upload file to S3 via presigned URL — returns publicUrl ── */
  const uploadFile = async (fileToUpload) => {
    setUploadProgress('uploading');

    // 1. POST /api/public/upload/presigned-url to get the presigned URL
    const presignRes = await apiClient.post('/upload/presigned-url', {
      folder: 'contacts',
      filename: fileToUpload.name,
      mimetype: fileToUpload.type,
      nom: fileToUpload.name,
    });

    const { uploadUrl, publicUrl } = presignRes.data.data;

    // 2. PUT file directly to S3 using the presigned URL
    const s3Res = await fetch(uploadUrl, {
      method: 'PUT',
      body: fileToUpload,
      headers: { 'Content-Type': fileToUpload.type },
    });

    if (!s3Res.ok) throw new Error('Échec de l\'envoi vers le serveur de stockage.');

    setUploadProgress('done');
    return publicUrl;
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!nom || !email || !subject || !msg.trim()) return;

    // Client-side file validation before any network call
    if (file) {
      if (!ALLOWED_MIMETYPES.includes(file.type)) {
        setError('Type de fichier non autorisé. Fichiers acceptés : Images, PDF, Word, Excel, TXT.');
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        setError('Le fichier dépasse la taille maximale autorisée (10 Mo).');
        return;
      }
    }

    setLoading(true);
    setError(null);
    try {
      let filePayload = {};

      if (file) {
        try {
          const publicUrl = await uploadFile(file);
          filePayload = {
            fichier_url: publicUrl,
            fichier_nom: file.name,
            fichier_type: file.type,
            fichier_taille: file.size,
          };
        } catch (uploadErr) {
          setUploadProgress('error');
          setError(
            uploadErr?.response?.data?.message ||
            'Échec du téléchargement du fichier. Veuillez réessayer.'
          );
          setLoading(false);
          return;
        }
      }

      await postContactMessage({
        nom,
        email,
        telephone,
        subject,
        message: msg,
        ...filePayload,
      });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Une erreur est survenue lors de l\'envoi.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="contact-sent d-flex flex-column h-100 align-items-center justify-content-center">
        <img src='./logo192.png' width={120} height={120} alt='logo' />
        <h3>Message envoyé !</h3>
        <p>Nous vous répondrons dans les plus brefs délais.</p>
        {mobile && (
          <button className="contact-back-link" onClick={() => { setSent(false); onBack && onBack(); }}>
            <FaArrowLeft size={12} /> Retour
          </button>
        )}
      </div>
    );
  }

  return (
    <form className="contact-form-inner" onSubmit={handleSend}>
      {mobile && (
        <button type="button" className="contact-back-link" onClick={onBack}>
          <FaArrowLeft size={12} /> Retour
        </button>
      )}

      {!mobile && (
        <>
          <h3 className="contact-form-title">Envoyez-nous un message</h3>
          <p className="contact-form-subtitle">Nous vous répondrons rapidement.</p>
        </>
      )}

      {error && (
        <div style={{ color: '#d32f2f', background: '#ffebee', padding: '10px 14px', borderRadius: 6, marginBottom: 20, fontSize: 13, fontWeight: 500 }}>
          {error}
        </div>
      )}

      <div className="contact-form-grid">
        <div className="form-field">
          <label htmlFor="contact-nom">Nom complet *</label>
          <input
            id="contact-nom"
            type="text"
            placeholder="Votre nom"
            value={nom}
            onChange={e => setNom(e.target.value)}
            required
            maxLength={100}
          />
        </div>

        <div className="form-field">
          <label htmlFor="contact-email">E-mail *</label>
          <input
            id="contact-email"
            type="email"
            placeholder="votre@email.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="form-field">
          <label htmlFor="contact-telephone">Téléphone <span style={{ fontWeight: 400, color: '#aaa' }}>(facultatif)</span></label>
          <input
            id="contact-telephone"
            type="tel"
            placeholder="Votre numéro de téléphone"
            value={telephone}
            onChange={e => setTelephone(e.target.value)}
            maxLength={30}
          />
        </div>

        <div className="form-field">
          <label htmlFor="contact-subject">Sujet *</label>
          <select
            id="contact-subject"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            required
          >
            <option value="">Choisissez un sujet</option>
            {SUBJECTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>

      <div className="form-field">
        <label htmlFor="contact-msg">Votre message *</label>
        <textarea
          id="contact-msg"
          placeholder="Écrivez votre message ici..."
          value={msg}
          onChange={e => msg.length < 1000 && setMsg(e.target.value)}
          rows={3}
          required
          minLength={10}
        />
        <div className="char-count">{msg.length}/1000</div>
      </div>

      <div className="form-field">
        <label>
          Joindre un fichier{' '}
          <span style={{ fontWeight: 400, color: '#aaa' }}>(facultatif)</span>
        </label>
        <div
          className={`drop-zone${file ? ' drop-zone--has-file' : ''}`}
          onClick={() => document.getElementById('contact-file-input').click()}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); setFile(e.dataTransfer.files[0]); }}
        >
          <div className="drop-icon"><FaPaperclip /></div>
          {file ? (
            <div style={{ color: '#333', fontWeight: 600 }}>
              {file.name}
              <span style={{ marginLeft: 8, fontSize: 11, color: '#888', fontWeight: 400 }}>
                ({(file.size / 1024 / 1024).toFixed(2)} Mo)
              </span>
            </div>
          ) : (
            <>
              <div>Cliquez pour ajouter un fichier<br />ou glissez-déposez ici</div>
              <div style={{ marginTop: 4, color: '#aaa' }}>Images, PDF, Word, Excel, TXT (max. 10 Mo)</div>
            </>
          )}
          <input
            id="contact-file-input"
            type="file"
            accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt"
            style={{ display: 'none' }}
            onChange={e => { setUploadProgress(null); setFile(e.target.files[0]); }}
          />
        </div>
      </div>

      <button type="submit" className="btn-submit" disabled={loading}>
        {loading
          ? (uploadProgress === 'uploading' ? 'TÉLÉCHARGEMENT...' : 'ENVOI EN COURS...')
          : <><FaPaperPlane /> ENVOYER LE MESSAGE</>
        }
      </button>
    </form>
  );
}

/* ── Main page ── */
export default function ContactPage() {
  const [showForm, setShowForm] = useState(false);

  return (
    <>
      {/* ── DESKTOP layout ── */}
      <div className="contact-desktop">
        {/* Left: heading + contact info */}
        <div className="contact-desktop-left">
          <div className="contact-desktop-header">
            <span className="contact-badge-tag">
              <FiMessageCircle size={12} /> Contact
            </span>
            <h2 className="contact-desktop-title">
              Comment pouvons-nous<br />vous aider ?
            </h2>
            <p className="contact-desktop-sub">
              Notre équipe est là pour répondre à toutes vos questions et vous accompagner.
            </p>
          </div>

          <div className="contact-info-rows">
            {CONTACTS.map((c, i) => (
              c.label === 'Support par e-mail' ? null : (
                <a key={i} href={c.href} className="contact-info-row" target="_blank" rel="noreferrer">
                  <div className="contact-info-icon">{c.icon}</div>
                  <div>
                    <div className="contact-info-label">{c.label}</div>
                    <div className="contact-info-value">{c.value}</div>
                  </div>
                </a>
              )
            ))}
          </div>
        </div>

        {/* Right: floating form card */}
        <div className="contact-desktop-right">
          <div className="contact-form-card">
            <MessageForm />
          </div>
        </div>
      </div>

      {/* ── MOBILE layout ── */}
      <div className="contact-mobile">
        {showForm ? (
          <div className="contact-form-page">
            <MessageForm mobile onBack={() => setShowForm(false)} />
          </div>
        ) : (
          <div className="contact-page">
            <h3 className="contact-title">Nous contacter</h3>
            <p className="contact-subtitle">
              Si vous avez des questions, n'hésitez pas à nous contacter. Nous serons ravis de vous aider.
            </p>

            {CONTACTS.map((c, i) => (
              <a 
                key={i} 
                href={c.label === 'Support par e-mail' ? '#' : c.href}
                onClick={c.label === 'Support par e-mail' ? (e) => { e.preventDefault(); setShowForm(true); } : undefined}
                className="contact-item" 
                target={c.label === 'Support par e-mail' ? undefined : '_blank'}
                rel={c.label === 'Support par e-mail' ? undefined : 'noreferrer'}
              >
                <div className="contact-item-left">
                  <span className="contact-item-icon">
                    {c.icon}
                  </span>
                  <div>
                    <div className="contact-item-label">{c.label}</div>
                    <div className="contact-item-value" style={c.label === 'Support par e-mail' ? { visibility: 'hidden', height: 0 } : undefined}>
                      {c.value}
                    </div>
                  </div>
                </div>
                <FaArrowRight className="contact-arrow" size={14} />
              </a>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
